"use client";

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type FormData = {
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  businessType: string;
  productInterest: string[];
  channelType: string;
  orderVolume: string;
  message: string;
};

const productOptions = ['日用品', '母嬰用品', '小家電', '廚房用具'];

export default function RegistrationForm() {
  const [form, setForm] = useState<FormData>({
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    businessType: '',
    productInterest: [],
    channelType: '',
    orderVolume: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProductToggle = (product: string) => {
    setForm((prev) => ({
      ...prev,
      productInterest: prev.productInterest.includes(product)
        ? prev.productInterest.filter((p) => p !== product)
        : [...prev.productInterest, product],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Mock submit
    await new Promise((r) => setTimeout(r, 1500));
    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-3xl border border-brand-border p-10 text-center shadow-card">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ background: 'linear-gradient(135deg, #F5EFE6, #C4915A)' }}
        >
          <Icon name="CheckIcon" size={40} className="text-primary" />
        </div>
        <h3 className="font-heading font-bold text-brand-fg text-2xl mb-3">報名成功！</h3>
        <p className="text-brand-muted leading-relaxed mb-6">
          感謝您的報名！我們的專屬客戶經理將在 <strong className="text-primary">24小時內</strong> 通過電話或郵件與您聯繫。
        </p>
        <p className="text-sm text-brand-muted">
          如有緊急需求，請直接致電：<strong className="text-primary">+86 21 6888 XXXX</strong>
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 px-6 py-2.5 border border-brand-border rounded-full text-sm font-medium text-brand-muted hover:text-primary hover:border-primary/30 transition-colors"
        >
          重新填寫
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-brand-border p-8 shadow-card">
      <h3 className="font-heading font-bold text-brand-fg text-2xl mb-6">合作夥伴報名表</h3>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Company & Contact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-fg mb-1.5">
              公司名稱 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="companyName"
              value={form.companyName}
              onChange={handleChange}
              placeholder="請輸入公司全稱"
              className="form-input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-fg mb-1.5">
              聯繫人姓名 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="contactName"
              value={form.contactName}
              onChange={handleChange}
              placeholder="請輸入聯繫人姓名"
              className="form-input"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-fg mb-1.5">
              聯繫電話 <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="+86 138 XXXX XXXX"
              className="form-input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-fg mb-1.5">
              電子郵件 <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="example@company.com"
              className="form-input"
              required
            />
          </div>
        </div>

        {/* Business type */}
        <div>
          <label className="block text-sm font-medium text-brand-fg mb-1.5">
            業務類型 <span className="text-red-400">*</span>
          </label>
          <select
            name="businessType"
            value={form.businessType}
            onChange={handleChange}
            className="form-input"
            required
          >
            <option value="">請選擇業務類型</option>
            <option value="wholesale">線下批發商</option>
            <option value="retail">零售商/商超</option>
            <option value="ecommerce">電商賣家</option>
            <option value="crossborder">跨境電商</option>
            <option value="other">其他</option>
          </select>
        </div>

        {/* Product interest */}
        <div>
          <label className="block text-sm font-medium text-brand-fg mb-2">
            感興趣的產品類別（可多選）
          </label>
          <div className="flex flex-wrap gap-2">
            {productOptions.map((product) => (
              <button
                key={product}
                type="button"
                onClick={() => handleProductToggle(product)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                  form.productInterest.includes(product)
                    ? 'bg-primary text-white border-primary' :'bg-white text-brand-muted border-brand-border hover:border-primary/40 hover:text-primary'
                }`}
              >
                {product}
              </button>
            ))}
          </div>
        </div>

        {/* Channel type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-brand-fg mb-1.5">合作渠道</label>
            <select name="channelType" value={form.channelType} onChange={handleChange} className="form-input">
              <option value="">請選擇</option>
              <option value="online">線上電商</option>
              <option value="offline">線下批發</option>
              <option value="both">線上+線下</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-brand-fg mb-1.5">預計月採購量</label>
            <select name="orderVolume" value={form.orderVolume} onChange={handleChange} className="form-input">
              <option value="">請選擇</option>
              <option value="small">1,000件以下</option>
              <option value="medium">1,000–10,000件</option>
              <option value="large">10,000件以上</option>
            </select>
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-brand-fg mb-1.5">補充說明</label>
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            placeholder="請描述您的業務情況或具體需求..."
            rows={3}
            className="form-input resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-primary text-white rounded-full font-bold text-base hover:bg-primary-dark transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              提交中...
            </>
          ) : (
            <>
              提交報名申請
              <Icon name="ArrowRightIcon" size={18} />
            </>
          )}
        </button>

        <p className="text-xs text-brand-muted text-center">
          提交即表示您同意我們的隱私政策，我們將保護您的個人信息安全。
        </p>
      </form>
    </div>
  );
}